import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  SegmentedTabs,
  type SegmentedTabItem,
} from '@/components/SegmentedTabs';
import { useResponsiveTabsWidth } from '@/hooks/useResponsiveTabsWidth';
import { useUnseenEvents } from '@/hooks/useUnseenEvents';
import { MailSection } from '@/types/components';

const MAX_TABS_WIDTH_PX = 440;

const unseenDot = (
  <span className="absolute -top-px -right-3 size-2 shrink-0 rounded-full bg-accent" />
);

interface Props {
  value: MailSection;
  onChange: (section: MailSection) => void;
}

export const MailSegmentedControl: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const { data: unseenEvents } = useUnseenEvents();
  const { ref, style } = useResponsiveTabsWidth({
    maxWidth: MAX_TABS_WIDTH_PX,
  });

  const showDot = (count = 0) => (count > 0 ? unseenDot : null);

  const items: Array<SegmentedTabItem<MailSection>> = [
    {
      value: MailSection.Mail,
      label: t('mail.sections.mail'),
      trailing: showDot(unseenEvents?.mailMessageCount),
    },
    {
      value: MailSection.Surveys,
      label: t('mail.sections.surveys'),
      trailing: showDot(unseenEvents?.surveyCount),
    },
    {
      value: MailSection.VideoCalls,
      label: t('mail.sections.videoCalls'),
      trailing: showDot(unseenEvents?.videoMeetingCount),
    },
  ];

  return (
    <div ref={ref} style={style} className="w-full md:w-fit">
      <SegmentedTabs
        items={items}
        value={value}
        className="flex w-full"
        tabClassName="flex-1"
        onChange={onChange}
      />
    </div>
  );
};
